import { useState } from "react";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { Reveal, SectionHead } from "./Section";
import { PLANS, money } from "@/lib/plans";

const INCLUDED = ["Rosters and shift alerts", "Clock on from the phone", "Timesheets and payroll PDFs", "Job photos and client reports", "Your logo and colours"];

const BillingToggle = ({ yearly, setYearly }) => (
  <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="pricing-billing-toggle">
    {[[false, "Monthly"], [true, "Yearly · 2 months free"]].map(([v, label]) => (
      <button key={label} onClick={() => setYearly(v)} data-testid={`pricing-billing-${v ? "yearly" : "monthly"}`}
        className={`rounded-full px-4 sm:px-5 py-2 text-sm font-semibold transition-colors ${yearly === v ? "bg-[#0B1120] text-white" : "text-slate-600 hover:text-[#0F172A]"}`}>{label}</button>
    ))}
  </div>
);

const PlanCard = ({ plan, yearly, onStart, i }) => {
  const dark = plan.popular;
  return (
    <Reveal delay={0.08 * i} className="h-full">
      <div className={`relative h-full flex flex-col rounded-2xl p-7 sm:p-8 border transition-transform hover:-translate-y-1 ${dark ? "bg-[#0B1120] text-white border-[#0B1120] shadow-2xl" : "bg-white border-slate-200"}`} data-testid={`pricing-plan-${plan.id}`}>
        {dark && <span className="absolute -top-3 left-7 rounded-full bg-[#EA580C] px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">Most popular</span>}
        <h3 className="font-display text-xl font-bold">{plan.name}</h3>
        <p className={`mt-1 text-sm ${dark ? "text-slate-400" : "text-slate-600"}`}>{plan.blurb}</p>
        <div className="mt-6 flex items-baseline gap-1.5">
          <span className="font-display text-5xl font-extrabold tracking-tight" data-testid={`pricing-price-${plan.id}`}>{money(yearly ? plan.price * 10 : plan.price)}</span>
          <span className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>/{yearly ? "year" : "month"} + GST</span>
        </div>
        <p className={`mt-1 text-xs h-4 ${dark ? "text-[#FDBA74]" : "text-[#EA580C]"}`}>{yearly ? `Save ${money(plan.price * 2)} a year` : ""}</p>
        <ul className="mt-6 mb-8 space-y-2.5 text-sm">
          {plan.limits.map((l) => <li key={l} className="flex items-center gap-2.5 font-semibold"><Check className="w-4 h-4 text-[#EA580C]" />{l}</li>)}
        </ul>
        <button onClick={onStart} data-testid={`pricing-start-${plan.id}`}
          className={`group mt-auto inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-bold transition-colors ${dark ? "bg-[#EA580C] text-white hover:bg-[#C2410C]" : "bg-[#1E3A5F] text-white hover:bg-[#172E4B]"}`}>
          Start 30-day free trial <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </Reveal>
  );
};

export const Pricing = ({ onStart, num = "06" }) => {
  const [yearly, setYearly] = useState(false);
  return (
    <section id="pricing" className="scroll-mt-16 bg-white py-24 lg:py-32" data-testid="home-pricing">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHead num={num} eyebrow="Pricing" title="Simple pricing. Every feature, every plan." sub="Try it free for 30 days, no card needed. Prices in Australian dollars." />
          <BillingToggle yearly={yearly} setYearly={setYearly} />
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((p, i) => <PlanCard key={p.id} plan={p} yearly={yearly} onStart={onStart} i={i} />)}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600" data-testid="pricing-included">
            <span className="font-semibold text-[#0F172A]">All plans include:</span>
            {INCLUDED.map((x) => <span key={x} className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#EA580C]" />{x}</span>)}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 overflow-hidden rounded-2xl bg-[#0B1120] text-white" data-testid="pricing-founding-offer">
            <div className="h-1.5 home-hazard" />
            <div className="flex flex-wrap items-center justify-between gap-6 p-7 sm:p-8">
              <div className="flex items-start gap-4">
                <Sparkles className="w-6 h-6 mt-1 text-[#EA580C] shrink-0" />
                <div>
                  <p className="font-display text-xl sm:text-2xl font-bold">Founding customer offer: 30% off for life.</p>
                  <p className="mt-1 text-slate-400">The first 10 companies to join keep 30% off their plan for as long as they stay with us.</p>
                </div>
              </div>
              <button onClick={onStart} data-testid="pricing-founding-start-btn" className="rounded-full border border-white/30 px-6 py-3 font-semibold hover:bg-white hover:text-[#0B1120] transition-colors">Claim a founding spot</button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
